import {Component, Inject, Input, PLATFORM_ID, ViewChild} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {CountrySelectorService, CountrySelectorStatus} from '@app/services/country-selector.service';
import {Country, Product, RoutingService} from '@spartacus/core';
import {isPlatformBrowser} from '@angular/common';

@Component({
  selector: 'dcp-country-switch-modal',
  templateUrl: './country-switch-modal.component.html',
})
export class CountrySwitchModalComponent {
  public static opened = false;
  @Input() countries: Country[];
  @ViewChild('modalContent') modalContentRef: NgbModal;
  public requestedCountry: string;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private routingService: RoutingService,
    private modalService: NgbModal,
    private countrySelectorService: CountrySelectorService
  ) {
  }

  /**
   * Opens a model to confirm the country switch if the product is not supported in the country.
   *
   * @param content HTML code of the modal body.
   */
  open(content: any): NgbModalRef {
    return this.modalService.open(content, {
      size: 'lg', backdrop: 'static', keyboard: false, centered: true
    });
  }

  /**
   * Finds a country by its iso code in the 'this.countries' list.
   *
   * @param isoCode iso code to filter the countries by.
   */
  getCountryByIsocode(isoCode: string): Country {
    return this.countries.find(c => c.isocode === isoCode);
  }

  /**
   * Opens a model to confirm the country switch if the product is not supported in the country.
   *
   * @param countryIsoCode country to switch to.
   * @param currentProduct product that is currently visited.
   */
  confirmCountryChange(countryIsoCode: string, currentProduct: Product): Promise<string> {
    return this.countrySelectorService.isProductAvailableInCountry(countryIsoCode, currentProduct.code)
      .then((isProductAvailable) => {
        if (isProductAvailable) {
          return CountrySelectorStatus.PRODUCT_ALLOWED;
        } else {
          /**
           * prevents modal from opening twice
           */
          if (CountrySwitchModalComponent.opened) {
            return;
          }
          CountrySwitchModalComponent.opened = true;
          this.requestedCountry = this.getCountryByIsocode(countryIsoCode)?.name;
          const modalRef: NgbModalRef = this.open(this.modalContentRef);
          return this.handleUsersChoiceOnModalClose(modalRef);
        }
      });
  }

  /**
   * The user needs to choose weather to leave the page or not.
   * This function registers a listener on the modal close event and gets the reason why the modal was closed.
   *
   * @param modalRef reference to the opened country switch modal.
   */
  handleUsersChoiceOnModalClose(modalRef: NgbModalRef): Promise<string> {
    return modalRef.result
      .then((reason) => {
        CountrySwitchModalComponent.opened = false;
        return this.handleUsersChoice(reason);
      });
  }

  /**
   * This function decides what action to take for which modal close reason.
   *
   * @param reason String that identifies the button the user clicked on.
   */
  handleUsersChoice(reason: string): string {
    if (reason === CountrySelectorStatus.CONFIRM_AND_VISIT_HOMEPAGE) {
      this.routingService.go({cxRoute: 'home'}).then(() => {
          if (isPlatformBrowser(this.platformId) && window?.location) {
            window.location.reload();
          }
        }
      );
      this.modalService.dismissAll();
    }
    return reason;
  }
}
